import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const Hero = () => {
  const [time, setTime] = useState("");

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      setTime(`${hours}:${minutes} WIB`);
    };
    update();
    const t = setInterval(update, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-ink grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1542401886-65d6c61db217?auto=format&fit=crop&w=1920&q=80"
          alt="Padang pasir di malam hari"
          className="h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/70 to-ink" />
      </div>

      {/* Top bar */}
      <header className="relative z-20 flex items-center justify-between px-6 py-6 md:px-12 md:py-8">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="flex items-center gap-3"
        >
          <div className="h-8 w-8 rounded-full border border-copper/60 flex items-center justify-center">
            <span className="font-serif text-copper text-lg leading-none">ﷺ</span>
          </div>
          <span
            data-testid="brand-mark"
            className="font-serif text-parchment text-xl tracking-wider"
          >
            Sirah&nbsp;Nabawiyah
          </span>
        </motion.div>

        <motion.nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="hidden md:flex items-center gap-8 lg:gap-10 text-[11px] uppercase tracking-[0.25em] text-warm-muted"
        >
          <a href="#fase-571" className="hover:text-parchment transition">
            Pra-Kenabian
          </a>
          <a href="#fase-610" className="hover:text-parchment transition">
            Wahyu
          </a>
          <a href="#fase-1H" className="hover:text-parchment transition">
            Hijrah
          </a>
          <a href="#fase-BADAR" className="hover:text-parchment transition">
            Badar
          </a>
          <a href="#fase-FATHU" className="hover:text-parchment transition">
            Fathu
          </a>
          <a href="#fase-11H" className="hover:text-parchment transition">
            Akhir Hayat
          </a>
        </motion.nav>

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="text-[11px] uppercase tracking-[0.25em] text-warm-muted"
        >
          {time}
        </motion.div>
      </header>

      {/* Hero content */}
      <div className="relative z-10 flex min-h-[calc(100vh-100px)] flex-col justify-end px-6 pb-24 md:px-12 md:pb-32">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8 lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="block text-[11px] uppercase tracking-[0.4em] text-copper mb-6"
              data-testid="hero-eyebrow"
            >
              — Sebuah Perjalanan Reflektif
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-title"
              className="font-serif text-parchment leading-[0.95] tracking-tightest text-[3.4rem] sm:text-7xl md:text-8xl lg:text-[8.5rem]"
            >
              Sejarah <em className="not-italic text-copper">Kita</em>,
              <br />
              Sejarah <span className="italic font-light">Beliau</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1 }}
              data-testid="hero-subtitle"
              className="mt-10 max-w-xl text-base md:text-lg text-warm-muted leading-relaxed font-light"
            >
              Mari kita susuri jejak Rasulullah ﷺ — dari kelahiran di Mekkah,
              malam-malam Gua Hira, hingga peraduan terakhir di kamar 'Aisyah.
              Sebuah linimasa enam fase yang membentuk dunia kita hari ini.
            </motion.p>
          </div>

          {/* Right side meta */}
          <div className="col-span-12 md:col-span-4 lg:col-span-5 flex md:justify-end items-end">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="flex flex-col gap-4 text-right"
            >
              <div className="watermark text-[7rem] sm:text-[9rem] md:text-[12rem] leading-none -mb-4 self-end">
                01
              </div>
              <span className="text-[11px] uppercase tracking-[0.3em] text-warm-muted">
                Mulai dari Mekkah
              </span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-copper">
                571 — 11 H · 6 Fase
              </span>
            </motion.div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-20 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-6 md:col-span-3">
            <div className="hair mb-3" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-warm-muted">
              Vertical Parallax · Editorial
            </span>
          </div>
          <div className="hidden md:block md:col-span-6" />
          <motion.a
            href="#fase-571"
            data-testid="hero-scroll-cta"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="col-span-6 md:col-span-3 flex md:justify-end items-center gap-3 text-parchment hover:text-copper transition group"
          >
            <span className="text-[11px] uppercase tracking-[0.3em]">
              Telusuri Linimasa
            </span>
            <span className="h-10 w-10 rounded-full border border-parchment/40 group-hover:border-copper flex items-center justify-center transition">
              <ChevronDown size={16} className="animate-flicker" />
            </span>
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default Hero;
